import { Playbook } from '../types';
import { PLAYBOOKS_URL } from '../config/api-config';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export const usePlaybooksApi = () => {

  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const getPlaybooks = async (getAllPlaybooks: boolean = false): Promise<Playbook[]> => {
    try {
      const headers = await getHeaders();

      const response = await fetch(
        `${PLAYBOOKS_URL}?getAllPlaybooks=${getAllPlaybooks}`,
        {
          method: 'GET',
          headers
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch Playbooks');
      }
      return response.json();
    } catch (error) {
      console.error('Error fetching Playbooks:', error);
      throw error;
    }
  };

  const getPlaybook = async (id: string): Promise<Playbook> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${id}`,
        {
          method: 'GET',
          headers
        }
      );
      console.log('[getPlaybook] Response:', response);

      if (!response.ok) {
        let errorMessage = 'Failed to fetch Playbook';
        let errorDetails;
        
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching Playbook:', error);
      // If it's already an ApiError, rethrow it
      if (error instanceof ApiError) {
        throw error;
      }
      // Otherwise wrap it in an ApiError
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  const createPlaybook = async (playbookAgenticgenie: Omit<Playbook, 'id'>): Promise<Playbook> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(playbookAgenticgenie),
        }
      );

      if (!response.ok) {
        console.log('[createPlaybook] Error creating Playbook response:', response);
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          console.log('[createPlaybook] Error creating Playbook errorData:', errorData);
          throw new Error(errorData.message || 'Failed to create Playbook');
        } catch (parseError) {
          console.log('[createPlaybook] Error parsing JSON:', String(parseError));
          console.log('[createPlaybook] Error parsing JSON Type:', typeof (parseError));
          throw new Error(String(parseError));
        }
      }
      return response.json();
    } catch (error) {
      console.error('Error creating Playbook:', error);
      throw error;
    }
  };

  const updatePlaybook = async (playbookAgenticgenie: Partial<Playbook>): Promise<Playbook> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${playbookAgenticgenie.playbookId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(playbookAgenticgenie),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update Playbook');
      }
      return response.json();
    } catch (error) {
      console.error('Error updating Playbook:', error);
      throw error;
    }
  };

  const deletePlaybook = async (id: string): Promise<void> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${id}`,
        {
          method: 'DELETE',
          headers,
        }
      );
      if (!response.ok) {
        throw new Error('Failed to delete Playbook');
      }
      return;
    } catch (error) {
      console.error('Error deleting Playbook:', error);
      throw error;
    }
  };

  return {
    getPlaybooks,
    getPlaybook,
    createPlaybook,
    updatePlaybook,
    deletePlaybook
  };
}